import React from "react";
import moment from "moment";
import { ReactComponent as ExpandSnapshot } from "../../../assets/icons/snapshotExpandedView.svg";
import { useNavigate } from "react-router-dom";
import { capitalizeFirstLetter } from "../../../utils/commonUtils";

interface SnapshotList {
  id: number;
  name: string;
  ownerId: string;
  owner_name: string;
  generic_filters: {
    preferenceFilters: {
      gender?: string;
      source?: string;
      startDate?: string;
      endDate?: string;
      region?: string;
      genre?: string;
      product?: string;
      brands?: string[];
      category?: string;
      color?: string;
    };
    trendAnalysisFilters: {
      material: string;
      product_type: string;
      styling_details: string;
    };
  };
  images: string[];
  trendsCount: number;
  sharedCount: number;
  created_at: string;
  updatedAt: string;
  status?: string;
}
interface SnapshotCardProps {
  data: SnapshotList;
  getImageLink: (image: string) => string;
  isSidebarOpen: boolean;
}

const SnapshotCard: React.FC<SnapshotCardProps> = ({
  data,
  getImageLink,
  isSidebarOpen,
}) => {
  const navigate = useNavigate();
  const status = data?.status;

  const handleNavigate = (id: number) => {
    if (status && status !== "completed") return;
    navigate(`/trend-snapshot/details/${id}`);
  };

  const isNotClickable = status && status !== "completed";

  return (
    <div
      key={data.id}
      className={`w-[172px] h-[275px] border rounded-md relative ${
        isNotClickable
          ? "border-gray-300 cursor-default"
          : "border-light cursor-pointer"
      }`}
      onClick={() => {
        handleNavigate(data?.id);
      }}
    >
      {/* Status badge */}
      {status === "failed" && (
        <div className="absolute top-2 left-2 z-10 bg-red-500 text-white text-[10px] font-poppins font-semibold px-2 py-0.5 rounded">
          Failed
        </div>
      )}
      {(status === "pending" || status === "processing") && (
        <div className="absolute inset-0 bg-gray-200/60 z-10 rounded-md flex items-center justify-center">
          <div className="flex flex-col items-center gap-1">
            <svg
              className="animate-spin h-6 w-6 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            <span className="text-[10px] text-gray-500 font-poppins">
              {status === "processing" ? "Processing..." : "Pending..."}
            </span>
          </div>
        </div>
      )}
      <div
        className={`h-[172px] w-full flex gap-2 ${isNotClickable ? "opacity-50" : ""}`}
      >
        <div className="h-full w-[108px]">
          <img
            src={getImageLink(data?.images?.[0] || "")}
            className="h-full w-full rounded-lg object-cover"
            alt="Snapshot"
          />
        </div>
        <div className="flex flex-col gap-2 h-[168px] w-[60px] overflow-hidden">
          {data?.images?.slice(1, 4)?.map((image, idx) => (
            <div key={idx} className="h-[56px]">
              <img
                src={getImageLink(image)}
                className="rounded-md object-cover h-full w-full"
                alt={`Snapshot ${idx + 1}`}
              />
            </div>
          ))}
        </div>
      </div>
      <div className={`w-full px-2 ${isNotClickable ? "opacity-50" : ""}`}>
        <div className="text-[14px] font-poppins font-bold truncate">
          {capitalizeFirstLetter(data?.name) || "N/A"}
        </div>
        <div className="text-[12px] font-poppins truncate">
          Gender: {data?.generic_filters?.preferenceFilters?.gender}
        </div>
        <div className="text-[12px] font-poppins truncate">
          Category:{" "}
          {data?.generic_filters?.preferenceFilters?.category || "N/A"}
        </div>
        <div className="text-[12px] font-poppins truncate">
          Owner: {data?.owner_name || "N/A"}
        </div>
        <div className="text-[12px] font-poppins text-tertiary">
          Date:{" "}
          {data?.created_at
            ? moment(data?.created_at).format("DD-MM-YYYY")
            : "N/A"}
        </div>
      </div>
      {!isNotClickable && (
        <div className="absolute bottom-2 right-2">
          <ExpandSnapshot />
        </div>
      )}
    </div>
  );
};

export default SnapshotCard;
